import { foldIndexesToSpans, foldCriteriasToSpans } from 'shared/excel/span';


// Each span is a SpansTree node
//
// 1-------------------------------------------200 +-->   the_whole_tree (index 1, end index 200). count = end index - index + 1 => count = 200
// |                                           |
// 1---------50  60--------------95*  120------200 +-->   With *: the_whole_tree.children[1] (index 60, end index 95, count 35)
// |         |    |              |      |      |
// 1--24  30-50  60--75 70-83 90-95*  120------200 +-->   With *: the_whole_tree.children[1].children[2] (index 90, end index 95 ,count 5)
//

export class SpansTree {
  constructor(index, count, children) {
    this.index = index ?? 0;
    this.count = count ?? 0;
    this.children = children ?? [];
    this._updateIndexes();
  }

  static getParentTreeFromIndexes(indexes) {
    return SpansTree.getParentTreeFromSpans(foldIndexesToSpans(indexes));
  }

  static getParentTreeFromSpans(spans) {
    return SpansTree.getParentTree(spans.map(x => SpansTree.fromSpan(x)));
  }

  static getParentTree(trees) {
    const { index, count } = SpansTree.getParentIndexes(trees);
    return new SpansTree(index, count, trees);
  }

  static getParentIndexes(trees) {
    if (!trees.length) { return {}; }
    const lower = trees[0];
    const upper = trees[trees.length - 1];
    return { index: lower.index, count: upper.index - lower.index + upper.count };
  }

  static fromSpan(span) {
    return new SpansTree(span.index, span.count);
  }

  merge(tree) {
    this.children.push(...tree.children);
    this._updateIndexes();
    return this;
  }

  addChild(tree) {
    if (!tree.isEmpty()) {
      this.children.push(tree);
      this._updateIndexes();
    }
    return this;
  }

  clone() {
    return new SpansTree(this.index, this.count, this.children.map(x => x.clone()));
  }

  reverse(basis) {
    this.index = basis - this.index - this.count;
    if (this.hasChildren()) {
      this.children.reverse().map(x => x.reverse(basis));
    }

    return this;
  }

  isEmpty() {
    return !this.count;
  }

  hasChildren() {
    return this.children && this.children.length;
  }

  unfoldToIndexes() {
    return this.hasChildren()
      ? this.children.flatMap(x => x.unfoldToIndexes())
      : (Array(this.count).fill(null).map((_, i) => this.index + i));
  }

  pickCount(countToPick) {
    return countToPick < 1
      ? new SpansTree()
      : this._pickCount(countToPick).toPick;
  }

  pickByCriteria(criterias) {
    const spansToPick = foldCriteriasToSpans(criterias);
    return this.pickByCriteriaSpans(spansToPick);
  }

  // spansToPick is array in format
  // [{ index: 6, count 5 }, { index: 20, count: 2 }]
  // Which means
  // 1. Skip 6 elements (append to the toLeft tree, 6 is because index - 0 = 6),
  // 2. then pick 5 elements (append to the toPick tree), 
  // 3. then skip 9 elements (20 - (6 + 5)),
  // 4. then pick 2 elements
  // 5. and if something else left append it to the toLeft
  pickByCriteriaSpans(spansToPick) { 
    const toPick = new SpansTree();
    const toLeft = new SpansTree();

    const valuesCount = this.getValuesCount();

    let lastIndex = 0;
    for (const { index, count } of spansToPick) {
      const toSkipCount = index - lastIndex;
      
      if (toSkipCount) {
        toLeft.merge(this.pickCount(toSkipCount));
      }
      toPick.merge(this.pickCount(count));
      lastIndex = index + count;
    }

    const toLeftCount = valuesCount - lastIndex;
    // if (lastIndex !== criterias.length) {
    if (toLeftCount > 0) {
      toLeft.merge(this.pickCount(toLeftCount));
    }

    return { toPick, toLeft };
  }

  flatten() {
    return this.hasChildren()
      ? this.children.reduce((out, x) => { out.push(...x.flatten()); return out; }, [])
      : [{ count: this.count, index: this.index }];
  }

  getValuesCount() {
    return this.hasChildren()
      ? this.children.reduce((out, x) => out += x.getValuesCount(), 0)
      : this.count;
  }

  getIndexAtPosition(position) {
    if (position < 0) { return null; }
    const res = this._getIndexAtPosition(position);
    return res.index ?? null;
  }

  _getIndexAtPosition(position) {
    if (!this.hasChildren()) {
      return position >= this.count
        ? { skipped: this.count }
        : { index: this.index + position };
    }

    let skipped = 0;
    for (const tree of this.children) {
      const { skipped: skippedInChildren, index } = tree._getIndexAtPosition(position);
      if (index !== undefined) { return { index }; }

      position -= skippedInChildren;
      skipped += skippedInChildren;
    }

    return { skipped };
  }

  _pickCount(count) {
    // If it's a leaf node
    if (!this.hasChildren()) {
      if (count > this.count) { return { picked: this.count }; }

      const toPick = new SpansTree(this.index, count);
      this.index += count;
      this.count -= count;

      return { picked: count, toLeft: this, toPick };
    }

    let picked = 0;

    for (let i = 0; i < this.children.length; ++i) {
      const { toPick: childToPick, toLeft: childToLeft, picked: pickedInChildren } = this.children[i]._pickCount(count);

      picked += pickedInChildren;
      count -= pickedInChildren;
      if (count > 0) { continue; } // continue picking

      const childrenToPick = this.children.splice(0, i);
      childrenToPick.push(childToPick);

      !childToLeft.isEmpty()
        ? this.children[0] = childToLeft // child is split
        : this.children.shift(); // child has completely gone

      if (!this.children.length) {
        this.count = 0; // Mark this node as empty
      }

      this._updateIndexes();
      return { picked, toPick: SpansTree.getParentTree(childrenToPick), toLeft: this };
    }

    // TODO
    // In case if this is a root of a tree and we pick more indexes than tree contains
    // then the tree will not be empty, but remains unchanged
    // const { toPick } = tree.pickCount(100); // tree contains 80 indexes
    // assert(toPick === tree) never fires => tree is unchanged. Probably it's a good idea to
    // compare this === toPick in SpansTree::pickCount and if they are equal move all nodes to another
    // tree and return it, setting this.count to 0 and this.children = []
    return { picked, toPick: this, toLeft: new SpansTree() };
  }

  _updateIndexes() {
    if (this.hasChildren()) {
      Object.assign(this, SpansTree.getParentIndexes(this.children));
    }
  }
}


export default SpansTree;